import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ValidationError } from 'yup';

import { AcceptingDefaultValueFieldBlueprint, Validations, fieldValidation } from '@zinnia/form-engine-sdk';

import { getValidationError } from '../../../../../../Helpers/inputValidation/getValidationError';
import { getLanguage } from '../../../../../../Localization/utils';

type Props<TBlueprint extends AcceptingDefaultValueFieldBlueprint> = {
  validateAs?: Validations;
  initialValue?: TBlueprint['defaultValue'];
};

export function useDefaultValue<TBlueprint extends AcceptingDefaultValueFieldBlueprint>({
  validateAs,
}: Props<TBlueprint>): {
  defaultValue: TBlueprint['defaultValue'];
  setDefaultValue: React.Dispatch<React.SetStateAction<TBlueprint['defaultValue']>>;
  defaultValueValidationError?: ValidationError;
} {
  const [defaultValue, setDefaultValue] = useState<TBlueprint['defaultValue']>();

  const { t } = useTranslation();
  const language = getLanguage();

  const fieldValueValidation = useMemo(() => {
    if (!validateAs) {
      return;
    }
    return fieldValidation(validateAs, false, undefined, language);
  }, [validateAs, t]);

  const validationError = useMemo(() => {
    if (!defaultValue || !fieldValueValidation) {
      return;
    }
    return getValidationError({ defaultValue: fieldValueValidation }, 'defaultValue', defaultValue);
  }, [fieldValueValidation, defaultValue]);

  const firstUpdate = useRef(true);

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
  }, []);

  return {
    defaultValue,
    setDefaultValue,
    defaultValueValidationError: firstUpdate.current === false ? validationError : undefined,
  };
}
